import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui core components
import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import NotificationsNoneTwoToneIcon from "@mui/icons-material/NotificationsNoneTwoTone";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import { Skeleton, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftAvatar from "components/SoftAvatar";
import { Grid } from "@mui/material";
import SoftBadge from "components/SoftBadge";
import Breadcrumbs from "./Breadcrumbs";
import NotificationItem from "pages/components/NotificationItem";
import PricingModal from "components/PricingModal";
// AppModeSwitcher was the navbar dropdown for picking a system; the Flex2
// logo on the sidebar is now the single switch entry point (opens
// DefaultSystemPicker). Import removed; component file kept on disk for
// safe rollback. The "Set as my home" pin button also lives on the
// sidebar now (EnvironmentalPortalSidebar's bottom org card) — the DashboardNavbar
// is backoffice-only chrome and doesn't carry user-facing system controls.
// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "./styles";

import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

import { useNavigate } from "react-router-dom";
// Placed with the other feature imports rather than at the top of the
// block: api/index.js participates in an import cycle with redux-store,
// so pulling the notifications graph in early can initialize it before
// react-redux.
import { NotificationsInboxApi } from "notifications/api/notifications.api";
import { logoutUser, signOut } from "redux-store/auth/actions";
import { getUser } from "redux-store/auth/actions";
import CartApiService from "api/cart.api";
import { initCart, updateCart } from "redux-store/cart/reducers";
import ProfileAvtar from "pages/components/ProfileAvtar";
import { handleUnAuthorizedAction } from "redux-store/auth/reducers";
import OrganizationListModal from "./modal";
import {
  afmsNavbarContainerSx,
  afmsNavbarSx,
  isAfmsPath,
} from "afms/theme/commandCenterTheme";
import {
  pickSidebarVariant,
  selectUserDetails,
  SIDEBAR_VARIANTS,
} from "config/appMode";

function DashboardNavbar({ absolute, light, isMini, customTitle }) {
  const [navbarType, setNavbarType] = useState();
  const [organizationListOpen, setOrganizationListOpen] = useState(false);
  const [controller, controllerDispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const { pathname } = useLocation();
  // Suppress this entire component when the app is in a portal shell.
  // Two signals:
  //   1. Path-based — any /afms/* route. Covers the AFMS-native pages
  //      regardless of org (the legacy backoffice can also hit them).
  //   2. Shell-variant — the user's pickSidebarVariant is one of the
  //      portal variants. Covers cross-portal pages like /assets/add
  //      and /assets/edit when they're rendered inside a portal shell —
  //      the shell already supplies the header chrome, so this navbar
  //      is redundant and visually competes with the portal sidebar.
  //
  //   Historical note: this used to test SIDEBAR_VARIANTS.VENDOR_PORTAL.
  //   That constant was renamed to ENVIRONMENTAL_PORTAL during the Tier 1
  //   sidebar rename — the stale reference silently resolved to undefined,
  //   so the gate started returning false and the backoffice navbar leaked
  //   into the cioccaauto /assets/edit shell.
  const navbarUser = useSelector((state) => state.auth.user);
  const isPortalShell = (() => {
    const v = pickSidebarVariant(selectUserDetails(navbarUser));
    return (
      v === SIDEBAR_VARIANTS.ENVIRONMENTAL_PORTAL ||
      v === SIDEBAR_VARIANTS.LCP_PORTAL ||
      v === SIDEBAR_VARIANTS.ENERGY_PORTAL
    );
  })();
  const isAfms = isAfmsPath(pathname) || isPortalShell;
  // Where the unread-notifications item goes. The inbox is the same page and
  // the same notifications-api feed in every portal, but it is mounted per
  // portal, so send the user to the one they are actually in rather than
  // bouncing them into the environmental shell. Path first (it describes
  // where they are right now), then the user's own portal, then the
  // environmental mount as the fallback.
  const inboxPath = (() => {
    if (pathname.startsWith("/lcp")) return "/lcp/inbox";
    if (pathname.startsWith("/afms")) return "/afms/inbox";
    const variant = pickSidebarVariant(selectUserDetails(navbarUser));
    if (variant === SIDEBAR_VARIANTS.LCP_PORTAL) return "/lcp/inbox";
    return "/afms/inbox";
  })();
  if (isAfms) return null;
  const route = pathname.split("/").slice(1);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, user, authToken } = useSelector((state) => state.auth);

  const { redirectToLogin } = useSelector((state) => state.auth.handleUnAuthorized);
  const cart = useSelector((state) => state.cart);

  // Initialize cart on mount
  useEffect(() => {
    const initializeCart = async () => {
      if (cart.cartId) {
        try {
          const res = await CartApiService.getCart(cart.cartId);
          const cartData = res?.data?.data;
          const items = cartData?.productCartItems || [];
          dispatch(
            updateCart({
              cartId: cart.cartId,
              totalQuantity: items.length,
              items: items.map((i) => i.productId || i.product?.id),
            })
          );
        } catch {
          // Cart expired or invalid — create new one
          try {
            const res = await CartApiService.createCart();
            const newCartId = res?.data?.data?.guid;
            if (newCartId) dispatch(initCart({ cartId: newCartId }));
          } catch (e) {
            console.error("Failed to create cart", e);
          }
        }
      } else {
        try {
          const res = await CartApiService.createCart();
          const newCartId = res?.data?.data?.guid;
          if (newCartId) dispatch(initCart({ cartId: newCartId }));
        } catch (e) {
          console.error("Failed to create cart", e);
        }
      }
    };
    initializeCart();
  }, []);

  // console.log([loading, error, user, authToken]);F

  useEffect(() => {
    // console.log([loading, error, user, authToken]);

    if (error) {
      toast.error(error?.message ?? error);
      dispatch(logoutUser());
      setTimeout(() => {
        navigate("/login");
      }, 1000);
    }

    if (authToken && !user) {
      dispatch(getUser({ token: authToken }));
    }
  }, [error, user, authToken]);

  useEffect(() => {
    if (redirectToLogin) {
      navigate("/login");
    }

    return () => {
      dispatch(handleUnAuthorizedAction(false));
    };
  }, [redirectToLogin]);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(
        controllerDispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [controllerDispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(controllerDispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(controllerDispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  // profile drodown
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // organization modal open
  const handleOpenOrganization = () => {
    setOrganizationListOpen(true);
  };

  // organizationmodal close
  const handleOrganizationListClose = () => {
    setOrganizationListOpen(false);
  };

  // Unread inbox count for the bell. 30s, matching the interval
  // components/messages/useUnreadCounts.js already established, so the two
  // pollers stay in step rather than drifting into separate rhythms.
  // Best-effort: notifications-api may not be running in every environment,
  // and a dead poller must never break the navbar, so failures reset to 0
  // silently rather than surfacing an error on every page.
  const [unreadCount, setUnreadCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      NotificationsInboxApi.count()
        .then((res) => {
          if (cancelled) return;
          setUnreadCount(res?.data?.data?.unread ?? 0);
        })
        .catch(() => {
          if (!cancelled) setUnreadCount(0);
        });
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        title={[
          unreadCount === 0
            ? "no unread notifications"
            : `${unreadCount} unread — open inbox`,
        ]}
        onClick={() => {
          handleCloseMenu();
          navigate(inboxPath);
        }}
      />
    </Menu>
  );
  function notificationsLabel(count) {
    if (count === 0) {
      return "no notifications";
    }
    if (count > 99) {
      return "more than 99 notifications";
    }
    return `${count} notifications`;
  }
  return (
    <AppBar
      position={isAfms ? "sticky" : absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => (isAfms ? afmsNavbarSx : navbar(theme, { transparentNavbar, absolute, light }))}
    >
      <Toolbar sx={(theme) => (isAfms ? afmsNavbarContainerSx : navbarContainer(theme))}>
        <SoftBox
          color="inherit"
          mb={isAfms ? 0 : { xs: 1, md: 0 }}
          sx={(theme) => ({
            ...navbarRow(theme, { isMini }),
            ...(isAfms && {
              minWidth: 0,
              flex: "1 1 auto",
              justifyContent: "flex-start",
              [theme.breakpoints.up("xl")]: {
                justifyContent: "flex-start !important",
                width: "auto !important",
              },
            }),
          })}
        >
          <Breadcrumbs
            icon="home"
            title={customTitle || route[route.length - 1]}
            route={route}
            light={isAfms ? false : light}
          />
          <IconButton onClick={handleMiniSidenav} sx={navbarDesktopMenu}>
            <Icon fontSize="medium">
              {miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </IconButton>
        </SoftBox>
        {isMini ? null : (
          <SoftBox
            sx={(theme) => ({
              ...navbarRow(theme, { isMini }),
              ...(isAfms && {
                flex: "0 0 auto",
                justifyContent: "flex-end",
                [theme.breakpoints.up("xl")]: {
                  justifyContent: "flex-end !important",
                  width: "auto !important",
                },
              }),
            })}
          >
            <SoftBox color={isAfms ? "inherit" : light ? "white" : "inherit"}>
              <IconButton
                size="large"
                sx={navbarIconButton}
                onClick={() => navigate("/order-product/cart")}
                style={{ color: "#17c1e8", marginRight: 10 }}
              >
                <SoftBadge badgeContent={cart.totalQuantity} color="info" size="lg">
                  <ShoppingCartOutlinedIcon fontSize="medium" />
                </SoftBadge>
              </IconButton>
              <IconButton
                size="large"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
                style={{ color: "#ff8e7a", marginRight: 10 }}
                aria-label={notificationsLabel(unreadCount)}
              >
                <SoftBadge badgeContent={unreadCount} color="warning" size="lg">
                  <NotificationsNoneTwoToneIcon fontSize="medium" />
                </SoftBadge>
              </IconButton>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                <MenuItem component={Link} to="/account/subscriptions" onClick={handleClose}>
                  My Subscription
                </MenuItem>
                <MenuItem component={Link} to="/account/payment-methods" onClick={handleClose}>
                  Payment Methods
                </MenuItem>
                <MenuItem component={Link} to="/account/setting" onClick={handleClose}>
                  Account Settings
                </MenuItem>
                <MenuItem component={Link} to="/account/security" onClick={handleClose}>
                  Sign-in &amp; security
                </MenuItem>
                <MenuItem component={Link} to="/orders" onClick={handleClose}>
                  My Orders
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  component="button"
                  onClick={() => {
                    handleClose();
                    setPricingOpen(true);
                  }}
                >
                  Plans & Pricing
                </MenuItem>
                <MenuItem
                  component={Link}
                  to="/login"
                  onClick={() => {
                    dispatch(signOut());
                  }}
                >
                  Logout
                </MenuItem>
                {[2, 3].includes(user?.subscription?.subscriptionPlan?.id) && (
                  <MenuItem component="button" onClick={handleOpenOrganization}>
                    Switch Profile
                  </MenuItem>
                )}
              </Menu>
              <PricingModal
                open={pricingOpen}
                onClose={() => setPricingOpen(false)}
                organizationId={user?.organisation?.id || user?.organizationId}
              />
              <IconButton
                sx={navbarIconButton}
                size="small"
                id="basic-button"
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
              >
                {user ? (
                  <ProfileAvtar name={user?.name ?? "Loading.. "} />
                ) : (
                  <Skeleton variant="circular" width={40} height={40} />
                )}
                <Grid item xs={12} mx={1}>
                  <Grid sx={{ display: "flex", justifyContent: "start" }}>
                    <SoftTypography component="div" variant="h5" mx={0.9}>
                      {user ? user?.given_name : <Skeleton width={100} />}
                    </SoftTypography>
                  </Grid>
                  <Grid display={"flex"} flexDirection={"column"} textAlign={"start"}>
                    <SoftTypography component="div" variant="h6" mx={0.9} color="secondary" mb={"3px"}>
                      {user ? user?.email : <Skeleton width={150} />}
                    </SoftTypography>
                    {user?.userDetails?.subDomainName ? (
                      (() => {
                        const ud = user?.userDetails ?? {};
                        const sub = user?.subscription ?? {};
                        const plan = sub?.subscriptionPlan ?? {};
                        let jwtClaims = {};
                        try {
                          const t = localStorage.getItem("authToken");
                          if (t) {
                            const part = t.split(".")[1];
                            const padded = part + "===".slice((part.length + 3) % 4);
                            jwtClaims = JSON.parse(
                              atob(padded.replace(/-/g, "+").replace(/_/g, "/"))
                            );
                          }
                        } catch (e) {
                          jwtClaims = { __error: String(e) };
                        }
                        const diag = {
                          role: ud.role,
                          roleId: ud.roleId,
                          accountType: ud.accountType,
                          organizationId: ud.organizationId,
                          subDomainName: ud.subDomainName,
                          isVendorOrganization: ud.isVendorOrganization,
                          planId: plan.id ?? sub.planId,
                          planType: plan.planType,
                          planName: plan.name ?? ud.planName,
                          jwt: {
                            roleName: jwtClaims.roleName,
                            roleId: jwtClaims.roleId,
                            isPlatformAdmin: jwtClaims.isPlatformAdmin,
                            userType: jwtClaims.userType,
                            organizationId: jwtClaims.organizationId,
                          },
                        };
                        const tooltipBody = (
                          <SoftBox sx={{ p: 1, fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", maxWidth: 360 }}>
                            {JSON.stringify(diag, null, 2)}
                          </SoftBox>
                        );
                        const handleCopy = (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const txt = JSON.stringify(diag, null, 2);
                          if (navigator?.clipboard?.writeText) {
                            navigator.clipboard.writeText(txt).then(
                              () => toast.success("Auth diagnostics copied"),
                              () => toast.error("Copy failed — see console"),
                            );
                          }
                          // eslint-disable-next-line no-console
                          console.log("Auth diagnostics:", diag);
                        };
                        return (
                          <Tooltip title={tooltipBody} arrow placement="bottom-end">
                            <SoftTypography
                              component="div"
                              variant="h6"
                              mx={0.9}
                              color="text"
                              onClick={handleCopy}
                              sx={{ cursor: "pointer" }}
                            >
                              {`${ud.role}(${ud.subDomainName})`}
                            </SoftTypography>
                          </Tooltip>
                        );
                      })()
                    ) : (
                      <SoftTypography component="div" variant="h6" mx={0.9} color="text">
                        <Skeleton width={150} />
                      </SoftTypography>
                    )}
                  </Grid>
                </Grid>
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon className={light ? "text-white" : "text-dark"}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {renderMenu()}
            </SoftBox>
            {/* organization modal */}
            <OrganizationListModal
              organizationListOpen={organizationListOpen}
              setOrganizationListOpen={setOrganizationListOpen}
              handleOrganizationListClose={handleOrganizationListClose}
            />
          </SoftBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  customTitle: PropTypes.string,
};

export default DashboardNavbar;
